import type { QueryClient } from '@tanstack/react-query';
import { logger } from '@utils/logger';
import { questionEntityKey } from './queryKeys';
import type { QuestionCacheItem } from './types';
import { isQuestionCacheItem } from './types';
import { toQuestionCacheItem } from './mappers';
import type { QuestionDetailRead, QuestionProfileRead, QuestionRead } from '@models/api/question';

type AnyQuestionShape = QuestionRead | QuestionDetailRead | QuestionProfileRead;

export const upsertQuestionEntities = (
  queryClient: QueryClient,
  questions: AnyQuestionShape[],
): void => {
  questions.forEach(q => {
    const incoming = toQuestionCacheItem(q);
    const existing = queryClient.getQueryData<QuestionCacheItem>(questionEntityKey(q.id));
    const mergedDetail =
      incoming.detail && existing?.detail
        ? { ...existing.detail, ...incoming.detail }
        : (incoming.detail ?? existing?.detail);
    queryClient.setQueryData(questionEntityKey(q.id), {
      ...existing,
      ...incoming,
      detail: mergedDetail,
    });
  });
};

export const updateQuestionEntity = (
  queryClient: QueryClient,
  questionId: string,
  updater: (existing: QuestionCacheItem) => QuestionCacheItem,
): void => {
  const existing = queryClient.getQueryData(questionEntityKey(questionId));
  if (!isQuestionCacheItem(existing)) {
    if (import.meta.env.DEV) {
      logger.warn(
        `[entityStore] updateQuestionEntity no-op: question ${questionId} not in cache. ` +
          `The surface rendering this question hasn't seeded the entity store via upsertQuestionEntities.`,
      );
    }
    return;
  }
  queryClient.setQueryData(questionEntityKey(questionId), updater(existing));
};

export const getQuestionEntity = (
  queryClient: QueryClient,
  questionId: string,
): QuestionCacheItem | undefined => {
  const data = queryClient.getQueryData(questionEntityKey(questionId));
  return isQuestionCacheItem(data) ? data : undefined;
};
